import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { retailersAPI, getAgentsList } from '../services/api';
// ...existing code...
import { QRCodeSVG } from 'qrcode.react';
import './ManagementPages.css';

const RetailersPage = () => {
  const [retailers, setRetailers] = useState([]);
  const [documentsMap, setDocumentsMap] = useState({});
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [docsModalRetailer, setDocsModalRetailer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    shopName: '',
    email: '',
    phone: '',
    address: '',
    panNumber: '',
    aadhaarNumber: '',
    agentId: '',
  });
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);

  useEffect(() => {
      fetchRetailers();
      if (user?.role === 'ADMIN') {
        fetchAgents();
      }
  }, []);

    const fetchAgents = async () => {
      try {
        const data = await getAgentsList();
        // Handle different response formats and normalize agent shape
        let list = [];
        if (Array.isArray(data)) list = data;
        else if (data?.data && Array.isArray(data.data)) list = data.data;
        else if (data?.rows && Array.isArray(data.rows)) list = data.rows;

        const normalizedAgents = list.map(a => ({
          id: a.id ?? a.agent_id ?? a.user_id ?? a.userId ?? null,
          first_name: a.first_name ?? a.firstName ?? a.name ?? '',
          last_name: a.last_name ?? a.lastName ?? '',
          email: a.email ?? '',
          employee_id: a.employee_id ?? a.employeeId ?? '',
        })).filter(x => x.id !== null);

        setAgents(normalizedAgents);
      } catch (err) {
        setAgents([]); // fallback to empty
      }
    };

  const fetchRetailers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await retailersAPI.getRetailers();
      const list = data && (Array.isArray(data) ? data : data.data || []) || [];
      setRetailers(list);
      // Fetch documents for each retailer
      const docsMap = {};
      await Promise.all(
        list.map(async (ret) => {
          try {
            const docs = await retailersAPI.getDocuments(ret.id);
            // Normalize different possible response shapes to an array
            if (Array.isArray(docs)) {
              docsMap[ret.id] = docs;
            } else if (docs && Array.isArray(docs.data)) {
              docsMap[ret.id] = docs.data;
            } else if (docs && Array.isArray(docs.rows)) {
              docsMap[ret.id] = docs.rows;
            } else {
              docsMap[ret.id] = [];
            }
          } catch (e) {
            docsMap[ret.id] = [];
          }
        })
      );
      setDocumentsMap(docsMap);
    } catch (err) {
      setError(err.message || 'Failed to load retailers');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let payload = { ...formData };
      if (user?.role === 'ADMIN') {
        if (!formData.agentId) {
          alert('Please select an agent for this retailer.');
          return;
        }
        // Keep agentId as string (UUID) - don't convert to number
        payload.agentId = formData.agentId;
      }
      
      console.log('Submitting retailer update:', { 
        retailerId: selectedRetailer?.id, 
        payload,
        agentId: payload.agentId,
        agentIdType: typeof payload.agentId
      });
      
      if (selectedRetailer) {
        const result = await retailersAPI.updateRetailer(selectedRetailer.id, payload);
        console.log('Update result:', result);
        alert('Retailer updated successfully');
      } else {
        await retailersAPI.registerRetailer(payload);
        alert('Retailer registered successfully');
      }
      setShowForm(false);
      setSelectedRetailer(null);
      setFormData({
        name: '',
        shopName: '',
        email: '',
        phone: '',
        address: '',
        panNumber: '',
        aadhaarNumber: '',
        agentId: '',
      });
      fetchRetailers();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleEdit = (retailer) => {
    setSelectedRetailer(retailer);
    // Stringify agentId so it matches <option value> strings
    const agentIdVal = retailer.agent_id || retailer.agentId || (retailer.agent && retailer.agent.id) || '';
    setFormData({
      name: retailer.name,
      shopName: retailer.shop_name || '',
      email: retailer.email || '',
      phone: retailer.phone || '',
      address: retailer.address || '',
      panNumber: retailer.pan_number || '',
      aadhaarNumber: retailer.aadhaar_number || '',
      agentId: String(agentIdVal),
    });
    if (user?.role === 'ADMIN') fetchAgents();
    setShowForm(true);
  };

  const handleDelete = async (retailerId) => {
    if (window.confirm('Are you sure you want to delete this retailer?')) {
      try {
        await retailersAPI.deleteRetailer(retailerId);
        fetchRetailers();
      } catch (err) {
        alert('Error deleting retailer: ' + err.message);
      }
    }
  };

  const handleUpdateKyc = async (retailerId, status) => {
    if (!window.confirm(`Set KYC status to ${status} for this retailer?`)) return;
    try {
      await retailersAPI.updateKycStatus(retailerId, status);
      alert('KYC status updated');
      fetchRetailers();
    } catch (err) {
      alert('Error updating KYC status: ' + (err.message || err));
    }
  };

  const handleVerifyKyc = async (retailer) => {
    setQrLoading(true);
    try {
      const response = await retailersAPI.generateKycToken(retailer.id);
      setQrData({
        retailer,
        uploadUrl: response.uploadUrl,
        expiresAt: new Date(response.expiresAt),
      });
      setShowQrModal(true);
    } catch (err) {
      alert('Error generating QR token: ' + (err.message || err));
    } finally {
      setQrLoading(false);
    }
  };

  const handleOpenDocs = (retailer) => {
    setDocsModalRetailer(retailer);
    setShowDocsModal(true);
  };

  // Helper to open modal and fetch agents for admin
  const openRetailerForm = () => {
    setShowForm(true);
    setSelectedRetailer(null);
    setFormData({
      name: '',
      shopName: '',
      email: '',
      phone: '',
      address: '',
      panNumber: '',
      aadhaarNumber: '',
      agentId: '',
    });
    if (user?.role === 'ADMIN') {
      fetchAgents();
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Retailers Management</h1>
        <p>Manage all registered retailers and their information</p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button
          className="button button-primary"
          onClick={openRetailerForm}
        >
          Add New Retailer
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loader"></div>
      ) : retailers.length > 0 ? (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Shop Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Aadhaar</th>
                <th>PAN</th>
                <th>Active Loans</th>
                <th>Agent</th>
                <th>Documents</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {retailers.map((retailer) => {
                const docs = documentsMap[retailer.id] || [];
                return (
                  <tr key={retailer.id}>
                    <td>{retailer.name}</td>
                    <td>{retailer.shop_name}</td>
                    <td>{retailer.email}</td>
                    <td>{retailer.phone}</td>
                    <td>{retailer.aadhaar_number || retailer.aadhaar || retailer.aadhaarNumber || '-'}</td>
                    <td>{retailer.pan_number}</td>
                    <td>₹{(retailer.active_loan_amount || 0).toLocaleString()}</td>
                    <td>
                      {retailer.agent_name ? (
                        <span>
                          {retailer.agent_name}
                          {retailer.agent_employee_id ? ` (${retailer.agent_employee_id})` : ''}
                          {retailer.agent_email ? <div style={{fontSize: '12px', color: '#888'}}>{retailer.agent_email}</div> : null}
                        </span>
                      ) : <span style={{color:'#888'}}>N/A</span>}
                    </td>
                    <td>
                      {(() => {
                        let docsList = Array.isArray(docs) ? docs : (docs && Array.isArray(docs.data) ? docs.data : []);
                        if (!Array.isArray(docsList)) {
                          if (docsList && typeof docsList === 'object') {
                            docsList = Object.values(docsList);
                          } else {
                            docsList = [];
                          }
                        }
                        if (docsList.length === 0) return <button className="action-btn" disabled>No docs</button>;
                        return (
                          <button className="action-btn view-btn" onClick={() => handleOpenDocs(retailer)}>
                            View Documents ({docsList.length})
                          </button>
                        );
                      })()}
                    </td>
                    
                    <td>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(retailer.id)}
                      >
                        Delete
                      </button>
                      <button
                        className="action-btn verify-btn"
                        onClick={() => handleVerifyKyc(retailer)}
                        disabled={qrLoading}
                      >
                        {qrLoading ? 'Loading...' : 'Verify'}
                      </button>
                      <button
                        className="action-btn reject-btn"
                        onClick={() => handleUpdateKyc(retailer.id, 'REJECTED')}
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data">No retailers found</div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{selectedRetailer ? 'Edit Retailer' : 'Add New Retailer'}</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setSelectedRetailer(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Owner Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Rahul Sharma"
                  />
                </div>
                <div className="form-group">
                  <label>Shop Name *</label>
                  <input
                    type="text"
                    name="shopName"
                    value={formData.shopName}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., ABC Retail Store"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="10-digit mobile number"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="business@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>PAN Number</label>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={handleInputChange}
                    placeholder="ABCDE1234F"
                  />
                </div>
                <div className="form-group">
                  <label>Aadhaar Number</label>
                  <input
                    type="text"
                    name="aadhaarNumber"
                    value={formData.aadhaarNumber}
                    onChange={handleInputChange}
                    placeholder="12-digit Aadhaar"
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Complete business address"
                  />
                </div>
                {user?.role === 'ADMIN' && (
                  <div className="form-group">
                    <label>Select Agent *</label>
                    <select
                      name="agentId"
                      value={formData.agentId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Agent</option>
                      {agents.length === 0 ? (
                        <option value="" disabled>No agents found</option>
                      ) : (
                        agents.map(agent => (
                          <option key={agent.id} value={String(agent.id)}>
                            {agent.first_name || agent.name} {agent.last_name || ''} ({agent.email})
                          </option>
                        ))
                      )}
                    </select>
                    {agents.length === 0 && (
                      <div style={{ color: 'red', fontSize: '13px', marginTop: '4px' }}>
                        No agents available. Please add agents first.
                      </div>
                    )}
                  </div>
                )}
                <div className="form-actions">
                  <button type="submit" className="button button-primary">
                    {selectedRetailer ? 'Update' : 'Register'} Retailer
                  </button>
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => {
                      setShowForm(false);
                      setSelectedRetailer(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showDocsModal && docsModalRetailer && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Documents - {docsModalRetailer.name}</h2>
              <button className="close-btn" onClick={() => { setShowDocsModal(false); setDocsModalRetailer(null); }}>×</button>
            </div>
            <div className="modal-body">
              {(() => {
                const docs = documentsMap[docsModalRetailer.id] || [];
                let docsList = Array.isArray(docs) ? docs : (docs && Array.isArray(docs.data) ? docs.data : []);
                if (!Array.isArray(docsList)) {
                  if (docsList && typeof docsList === 'object') docsList = Object.values(docsList);
                  else docsList = [];
                }
                if (docsList.length === 0) return <div style={{ color: '#888' }}>No documents available.</div>;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {docsList.map((doc) => {
                      let fileUrl = doc?.file_url || doc?.fileUrl || '';
                      let href = fileUrl || '';
                      if (href && !/^https?:\/\//i.test(href)) {
                        if (!href.startsWith('/')) href = '/' + href;
                        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.kaammytra.com/api';
                        const backendOrigin = apiBaseUrl.replace(/\/api\/?$/i, '');
                        href = `${backendOrigin}${href}`;
                      }
                      if (!href || !/^https?:\/\//i.test(href)) href = '#';
                      return (
                        <a key={doc.id || doc.file_name} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (href === '#') e.preventDefault(); }} style={{ color: '#0066cc', fontSize: '14px', textDecoration: 'underline' }}>
                          {doc.document_type ? doc.document_type.replace(/_/g, ' ') : 'Document'} • {doc.file_name || ''}
                        </a>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="button button-secondary" onClick={() => { setShowDocsModal(false); setDocsModalRetailer(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showQrModal && qrData && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>KYC Verification - QR Code</h2>
              <button className="close-btn" onClick={() => setShowQrModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center' }}>
                <h3>{qrData.retailer.name}</h3>
                <p style={{ color: '#666', marginBottom: '20px' }}>Scan with mobile to upload documents</p>

                <div style={{
                  backgroundColor: '#f9f9f9',
                  padding: '20px',
                  borderRadius: '8px',
                  display: 'inline-block',
                  marginBottom: '20px'
                }}>
                  <QRCodeSVG
                    value={qrData.uploadUrl}
                    size={256}
                    level="H"
                    includeMargin={true}
                  />
                </div>

                <div style={{ marginBottom: '20px', textAlign: 'left', backgroundColor: '#f0f8ff', padding: '12px', borderRadius: '6px' }}>
                  <p><strong>Link expires in:</strong> 10 minutes</p>
                  <p><strong>Expires at:</strong> {qrData.expiresAt.toLocaleTimeString()}</p>
                  <p style={{ color: '#666', fontSize: '13px' }}>Or copy and open this link on mobile:</p>
                  <p style={{ wordBreak: 'break-all', color: '#0066cc', fontSize: '12px' }}>{qrData.uploadUrl}</p>
                </div>

                <div style={{ backgroundColor: '#fffbf0', padding: '12px', borderRadius: '6px', marginBottom: '20px', textAlign: 'left' }}>
                  <p><strong>Instructions:</strong></p>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    <li>Scan QR code with mobile</li>
                    <li>Upload PAN document</li>
                    <li>Upload Aadhaar document</li>
                    <li>KYC auto-verifies on successful upload</li>
                  </ul>
                </div>

                <div className="form-actions">
                  <button
                    className="button button-secondary"
                    onClick={() => setShowQrModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailersPage;
